import { MouseEvent, ReactNode } from 'react';
import { isCardBackgroundClick } from '../../../lib/card-background-click';
import { useBriefSlotState } from '../../../lib/use-brief-slot-state';
import { useNarrow } from '../../../lib/use-narrow';
import { Card } from '../ui';
import { Home2Brief, MaestroBrief, MobileMaestroBrief } from '../MaestroBrief';
import { ActivateBrief } from './ActivateBrief';
import { MobileActivateBrief } from './MobileActivateBrief';
import { MobileNewWorkspaceBrief } from './MobileNewWorkspaceBrief';
import { MobileUpgradeBrief } from './MobileUpgradeBrief';
import { NewWorkspaceBrief } from './NewWorkspaceBrief';
import { UpgradeBrief } from './UpgradeBrief';
import styles from './BriefSlot.module.css';

// The catch-up header's morning-brief slot — one of three states, picked by `resolveBriefState`
// (the native apps' decision, ported in lib/brief-state). Figma "Catch Up states" 7419:47452.
//
// The redesign puts the desktop brief inside a white DS Card at ~⅔ width beside the QR (the "card with
// buttons" — see `DesktopHomeHeader`). The mobile variants are already floating cards of their own
// (`MobileMaestroBrief` etc.), so on narrow the state renders bare.

// `home2` swaps the active-brief (`hero`) state for the redesigned `Home2Brief` card;
// every other state is untouched. The other surfaces keep their current brief.
export function BriefSlot({ home2 = false }: { home2?: boolean } = {}) {
  const narrow = useNarrow();
  // The resolution — and its `?briefState=` preview override — are shared with the Overview home's own
  // brief card (`lib/use-brief-slot-state.ts`), so the two homes cannot disagree about the state.
  const { state, workspace: firstWorkspace } = useBriefSlotState();

  let content: ReactNode = null;
  switch (state) {
    case 'hero':
      // Home 2 (desktop): the redesigned active-brief card. Mobile Home 2 keeps today's hero for now
      // (a mobile design lands later), so only the wide surface swaps.
      content = narrow ? <MobileMaestroBrief /> : home2 ? <Home2Brief /> : <MaestroBrief />;
      break;
    case 'newWorkspace':
      content = narrow ? (
        <MobileNewWorkspaceBrief workspace={firstWorkspace} />
      ) : (
        <NewWorkspaceBrief workspace={firstWorkspace} />
      );
      break;
    case 'activate':
      content = narrow ? <MobileActivateBrief /> : <ActivateBrief />;
      break;
    case 'upgrade':
      content = narrow ? <MobileUpgradeBrief /> : <UpgradeBrief />;
      break;
    default:
      return null;
  }

  if (narrow) {
    // `display: contents` adds no box, so the mobile card keeps its own layout; clicks still bubble here.
    return (
      <div className={`${styles.contents} ${styles.clickable}`} onClick={clickCardPrimary}>
        {content}
      </div>
    );
  }
  return (
    <Card interactive className={styles.clickable} onClick={clickCardPrimary}>
      {content}
    </Card>
  );
}

// A click anywhere on the card does what its main button does. Each state marks that button with
// `data-card-primary`, and the card clicks it, so the action and its BI beacon are the button's own.
function clickCardPrimary(e: MouseEvent<HTMLElement>) {
  if (!isCardBackgroundClick(e)) return;
  e.currentTarget.querySelector<HTMLElement>('[data-card-primary]')?.click();
}
